/** Organization-scoped estimating defaults stored in `metadata`. */
export const ORG_METADATA_SCOPE = "organization" as const;

export const ORG_SETTING_KEYS = {
    SalesTaxRate: "SalesTaxRate",
    GeneralInsurance: "GeneralInsurance",
    OverheadAmount: "OverheadAmount",
} as const;

export type OrgSettingKey = (typeof ORG_SETTING_KEYS)[keyof typeof ORG_SETTING_KEYS];

/** Display-only placeholders for the General Settings form before first save. */
export const ORG_SETTING_DEFAULTS = {
    SalesTaxRate: 7.5,
    GeneralInsurance: 3.25,
    OverheadAmount: 0,
} as const;

export type OrgEstimatingDefaults = {
    SalesTaxRate: number;
    GeneralInsurance: number;
    OverheadAmount: number;
};

export function parseMetaNumber(value: unknown, fallback: number): number {
    if (value == null) return fallback;
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function metaValueMap(
    rows: Array<{ MetaKey?: string; MetaValue?: unknown }> | null | undefined,
): Map<string, unknown> {
    const map = new Map<string, unknown>();
    for (const row of rows || []) {
        if (row?.MetaKey) map.set(row.MetaKey, row.MetaValue);
    }
    return map;
}

/**
 * Strict resolve for estimating/contract calc.
 * Throws if required org settings are missing — no silent defaults.
 */
export function resolveOrgEstimatingDefaults(
    rows: Array<{ MetaKey?: string; MetaValue?: unknown }> | null | undefined,
): OrgEstimatingDefaults {
    const map = metaValueMap(rows);
    const missing: string[] = [];

    for (const key of Object.values(ORG_SETTING_KEYS)) {
        if (!map.has(key)) missing.push(key);
    }

    if (missing.length) {
        throw new Error(
            `Organization estimating defaults are not configured (${missing.join(", ")}). Set them in Admin → General.`,
        );
    }

    const parseRequired = (value: unknown, label: string): number => {
        if (value === undefined || value === null || value === "") {
            throw new Error(`${label} is not configured in organization General Settings.`);
        }
        const n = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(n)) {
            throw new Error(`${label} is invalid in organization General Settings.`);
        }
        return n;
    };

    return {
        SalesTaxRate: parseRequired(
            map.get(ORG_SETTING_KEYS.SalesTaxRate),
            "Sales tax rate",
        ),
        GeneralInsurance: parseRequired(
            map.get(ORG_SETTING_KEYS.GeneralInsurance),
            "General insurance",
        ),
        OverheadAmount: parseRequired(
            map.get(ORG_SETTING_KEYS.OverheadAmount),
            "Overhead amount",
        ),
    };
}

/** Soft resolve for the General Settings editor (allows empty / first-time form). */
export function resolveOrgEstimatingDefaultsForEditor(
    rows: Array<{ MetaKey?: string; MetaValue?: unknown }> | null | undefined,
): OrgEstimatingDefaults {
    const map = metaValueMap(rows);
    return {
        SalesTaxRate: parseMetaNumber(
            map.get(ORG_SETTING_KEYS.SalesTaxRate),
            ORG_SETTING_DEFAULTS.SalesTaxRate,
        ),
        GeneralInsurance: parseMetaNumber(
            map.get(ORG_SETTING_KEYS.GeneralInsurance),
            ORG_SETTING_DEFAULTS.GeneralInsurance,
        ),
        OverheadAmount: parseMetaNumber(
            map.get(ORG_SETTING_KEYS.OverheadAmount),
            ORG_SETTING_DEFAULTS.OverheadAmount,
        ),
    };
}
